import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { ChevronLeft } from "lucide-react";

const UPDATED = "5 ottobre 2026";
const CONTACT = "info@mindleadsuite.com";

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
      <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

function Mail() {
  return (
    <a className="text-primary underline underline-offset-2" href={`mailto:${CONTACT}`}>
      {CONTACT}
    </a>
  );
}

/** Fornitori che trattano dati per conto di Mindlead (allineato all'Allegato A — DPA delle CGS). */
const PROCESSORS: { name: string; what: string; where: string }[] = [
  { name: "Supabase (infrastruttura AWS)", what: "database, autenticazione, archiviazione file e funzioni server", where: "Unione Europea (Parigi)" },
  { name: "Vercel", what: "hosting dell'applicazione web", where: "Unione Europea (Francoforte)" },
  { name: "Resend", what: "invio di email transazionali (inviti, notifiche)", where: "Stati Uniti" },
  { name: "Anthropic", what: "funzioni di intelligenza artificiale: assistente, analisi di documenti, bozze", where: "Stati Uniti" },
  { name: "OpenAI", what: "trascrizione delle registrazioni vocali dei rapportini e interpretazione del testo", where: "Stati Uniti" },
  { name: "Servizio di inoltro AI (Lovable AI Gateway)", what: "inoltro delle richieste di analisi di documenti PDF ai modelli di intelligenza artificiale", where: "Stati Uniti" },
  { name: "Sentry", what: "diagnostica: segnalazione di errori e misure di prestazione dell'app", where: "Unione Europea (Germania)" },
  { name: "OpenFreeMap e OpenStreetMap (Nominatim)", what: "mappe dei cantieri e conversione degli indirizzi in coordinate", where: "Unione Europea / Stati Uniti" },
  { name: "Fornitori di accesso ai dati bancari (open banking)", what: "lettura di movimenti e saldi dei conti, solo se il cliente attiva il collegamento bancario", where: "Unione Europea / Stati Uniti" },
  { name: "fattura-elettronica-api.it", what: "acquisizione e invio delle fatture elettroniche tramite SdI, solo se il cliente attiva la funzione", where: "Italia" },
];

/**
 * Informativa privacy dell'app Mindlead Construction (rotta pubblica /privacy).
 * Allineata alle CGS e al DPA (Allegati A e C). Testo da sottoporre a revisione legale prima della pubblicazione.
 */
export default function AppPrivacy() {
  const navigate = useNavigate();
  return (
    <main className="min-h-screen bg-background">
      <div className="mx-auto max-w-2xl space-y-8 px-5 py-10 pb-[calc(var(--safe-bottom,0px)+2.5rem)] pt-[calc(var(--safe-top,0px)+2.5rem)]">
        {Capacitor.isNativePlatform() && (
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/construction/cruscotto"))}
            className="-ml-1 inline-flex items-center gap-0.5 text-[17px] text-primary"
          >
            <ChevronLeft className="h-5 w-5" />
            Indietro
          </button>
        )}
        <header className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-widest text-primary">Mindlead Construction</p>
          <h1 className="text-2xl font-bold tracking-tight">Informativa sulla privacy dell'app</h1>
          <p className="text-sm text-muted-foreground">Ultimo aggiornamento: {UPDATED}</p>
        </header>

        <Section title="1. Chi siamo e ruoli">
          <p>
            Mindlead Construction è la piattaforma per la gestione di cantieri e commesse erogata da Digital Partner
            Lab LLC, società del Wyoming (Stati Uniti), 1021 E Lincolnway, Suite 7510, Cheyenne, WY 82001, che opera con
            il marchio Mindlead Suite ("Mindlead", "noi"). Contatto: <Mail />.
          </p>
          <p>
            Per i dati dell'account di accesso e per i dati tecnici e di sicurezza della piattaforma Mindlead è titolare
            del trattamento. Per i dati inseriti dall'azienda che utilizza il servizio (commesse, personale, rapportini,
            documenti, fatture e simili) il titolare è l'azienda cliente e Mindlead agisce come responsabile del
            trattamento, esclusivamente su istruzione documentata del cliente, ai sensi dell'art. 28 del Regolamento
            (UE) 2016/679 (GDPR) e dell'accordo sul trattamento dei dati sottoscritto con il cliente.
          </p>
        </Section>

        <Section title="2. Quali dati tratta l'app">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong className="text-foreground">Account:</strong> nome, cognome, indirizzo email, ruolo e azienda di
              appartenenza, credenziali di accesso e registri di accesso.
            </li>
            <li>
              <strong className="text-foreground">Dati operativi dell'azienda:</strong> dati anagrafici, di contatto e
              fiscali di dipendenti, collaboratori, committenti, fornitori e subappaltatori; presenze, ore lavorate e
              attività svolte; commesse, computi, SAL, rapportini, documenti di trasporto, fatture, certificazioni e
              documentazione di cantiere caricati dagli utenti.
            </li>
            <li>
              <strong className="text-foreground">Registrazioni vocali:</strong> quando si usa la dettatura del
              rapportino, l'audio viene trasmesso per essere trascritto e interpretato; serve solo a compilare il
              rapportino.
            </li>
            <li>
              <strong className="text-foreground">Foto e documenti:</strong> immagini scattate con la fotocamera o scelte
              dalla libreria per acquisire DDT, allegati e foto dei rapportini di cantiere.
            </li>
            <li>
              <strong className="text-foreground">Timbrature di cantiere (badge digitale):</strong> data, ora, cantiere
              e identificativo della persona (badge QR o inserimento da parte del caposquadra), registrati in un
              registro non modificabile; le correzioni sono annotazioni tracciate con motivo. Il lavoratore può
              timbrare per sé dall'app con il proprio account; il registro completo è visibile solo a chi gestisce il
              personale e ogni lavoratore vede solo le proprie timbrature. Non usiamo dati biometrici
              (riconoscimento facciale, impronte). Le timbrature fatte senza connessione sono salvate sul
              telefono e inviate appena torna la rete.
            </li>
            <li>
              <strong className="text-foreground">Posizione:</strong> una singola lettura puntuale, solo nel momento in
              cui l'utente la richiede (ad esempio per centrare la mappa) o in cui registra una timbratura, e
              <em> soltanto se l'azienda ha attivato la posizione sulle timbrature</em> dopo aver verificato la base
              giuridica (obbligo di legge, accordo sindacale o autorizzazione dell'Ispettorato), informato i lavoratori
              e svolto la valutazione d'impatto (DPIA). Non effettuiamo tracciamento continuo né in background e, se la
              funzione non è attivata, le coordinate vengono scartate dal server.
            </li>
            <li>
              <strong className="text-foreground">Dati bancari e fatture elettroniche:</strong> movimenti e saldi dei
              conti e fatture attive e passive, solo se il cliente attiva queste funzioni.
            </li>
            <li>
              <strong className="text-foreground">Dati tecnici e diagnostici:</strong> errori e prestazioni dell'app,
              tipo di dispositivo e sistema operativo, indirizzo IP e identificativo utente collegato all'errore.
            </li>
          </ul>
          <p>
            Il cliente si astiene dal caricare categorie particolari di dati (art. 9 GDPR) e dati relativi a condanne
            penali e reati, salvo quanto strettamente necessario e previa propria valutazione.
          </p>
        </Section>

        <Section title="3. Autorizzazioni del dispositivo">
          <p>
            L'app chiede l'accesso a fotocamera, libreria foto, microfono e posizione solo nel momento in cui si usa la
            funzione corrispondente. Ogni autorizzazione può essere revocata in qualsiasi momento da Impostazioni di iOS
            senza perdere l'accesso alle altre funzioni. Se la dettatura è in corso e lo schermo si blocca, la
            registrazione prosegue fino al termine; l'app non registra l'audio in nessun altro caso. Le timbrature e i rapportini
            compilati senza connessione restano sul dispositivo, in uno spazio privato dell'app, fino all'invio.
          </p>
        </Section>

        <Section title="4. Finalità e basi giuridiche">
          <p>
            Trattiamo i dati per erogare il servizio richiesto dal cliente (esecuzione del contratto), per garantire
            sicurezza e corretto funzionamento (legittimo interesse) e per adempiere a obblighi di legge. Non usiamo i
            dati per pubblicità e non li vendiamo.
          </p>
        </Section>

        <Section title="5. Nessun tracciamento">
          <p>
            L'app non contiene pubblicità, non utilizza identificativi pubblicitari e non traccia gli utenti tra app e
            siti di altre società. Per questo non è richiesta l'autorizzazione di tracciamento di Apple.
          </p>
        </Section>

        <Section title="6. Intelligenza artificiale">
          <p>
            Alcune funzioni usano modelli di intelligenza artificiale (assistente, analisi di documenti, trascrizione
            vocale, bozze di preventivo). Elaborano esclusivamente i dati presenti nell'azienda dell'utente per fornire
            la funzione richiesta. Gli output possono contenere errori: non costituiscono decisioni né consulenza
            tecnica, contabile, fiscale, giuslavoristica o legale e vanno verificati dall'utente prima dell'uso.
          </p>
        </Section>

        <Section title="7. Fornitori che trattano i dati per nostro conto">
          <p>
            Ci avvaliamo dei fornitori seguenti, nominati sub-responsabili del trattamento. Alcuni sono attivi solo se
            il cliente abilita la relativa funzione. L'elenco contrattuale è comunicato ai clienti, con preavviso in
            caso di modifica.
          </p>
          <ul className="list-disc space-y-1 pl-5">
            {PROCESSORS.map((p) => (
              <li key={p.name}>
                <strong className="text-foreground">{p.name}</strong> — {p.what}. <em>Ubicazione dei dati: {p.where}.</em>
              </li>
            ))}
          </ul>
          <p>
            Alcuni fornitori hanno sede negli Stati Uniti e possono accedere ai dati dal territorio statunitense; anche
            Mindlead ha sede negli Stati Uniti. I trasferimenti si fondano sulle Clausole Contrattuali Tipo della
            Commissione europea (Decisione di esecuzione (UE) 2021/914), con misure supplementari ove necessarie, o
            sul quadro di protezione dei dati UE-Stati Uniti per i fornitori certificati.
          </p>
        </Section>

        <Section title="8. Sicurezza">
          <p>
            Adottiamo cifratura dei dati in transito (TLS) e a riposo, separazione logica dei dati di ciascuna azienda,
            controllo degli accessi basato su ruoli e minimo privilegio, registrazione degli accessi, backup periodici e
            procedure di gestione degli incidenti. In caso di violazione dei dati personali del cliente lo informiamo
            senza ingiustificato ritardo e, ove possibile, entro 48 ore dalla conoscenza.
          </p>
        </Section>

        <Section title="9. Conservazione">
          <p>
            Conserviamo i dati per la durata del rapporto contrattuale. Alla cessazione l'accesso è disabilitato e i dati
            restano disponibili per l'esportazione per 90 giorni; poi vengono cancellati in modo irreversibile dagli
            ambienti di produzione e le copie di backup sono sovrascritte entro ulteriori 30 giorni, salvo obblighi di
            conservazione previsti dalla legge. I dati diagnostici sono conservati per il tempo strettamente necessario
            all'analisi dei malfunzionamenti.
          </p>
        </Section>

        <Section title="10. I tuoi diritti ed eliminazione dell'account">
          <p>
            Puoi chiedere accesso, rettifica, cancellazione, limitazione, portabilità e opposizione e proporre reclamo
            al Garante per la protezione dei dati personali. Per i dati operativi dell'azienda, di cui è titolare il tuo
            datore di lavoro o committente, rivolgiti al referente privacy della tua azienda: noi lo assistiamo nel dare
            seguito alle richieste.
          </p>
          <p>
            Puoi richiedere l'eliminazione del tuo account dall'app (icona account → Il mio account → Privacy e dati →
            "Elimina account") oppure scrivendo a <Mail />. La richiesta viene gestita entro 30 giorni.
          </p>
        </Section>

        <Section title="11. Minori">
          <p>Il servizio è destinato a utenti professionali maggiorenni e non è rivolto ai minori.</p>
        </Section>

        <Section title="12. Modifiche e contatti">
          <p>
            Possiamo aggiornare questa informativa; la versione in vigore è quella pubblicata a questo indirizzo, con la
            data di aggiornamento indicata in alto. Domande: <Mail />. L'informativa generale del sito è su{" "}
            <a
              className="text-primary underline underline-offset-2"
              href="https://mindleadsuite.com/legal/privacy-policy"
              target="_blank"
              rel="noreferrer"
            >
              mindleadsuite.com
            </a>
            .
          </p>
        </Section>
      </div>
    </main>
  );
}
